// http-client-factory.ts — R-06
// Fabrique partagée pour les instances axios de l'application. Centralise la
// configuration commune (baseURL, headers, timeout) et le mapping des erreurs
// ProblemDetails RFC 7807 → ApiError, afin d'éviter toute duplication entre
// client.ts (authentifié) et local-auth-client.ts (endpoints d'auth sans
// token préalable — décision R-01, deux instances distinctes pour éviter tout
// risque de régression, mais leur configuration commune ne doit être définie
// qu'à un seul endroit).

import axios from "axios";
import type { AxiosInstance, AxiosResponse, AxiosError } from "axios";

import { appConfig } from "@/config";
import {
  ApiError,
  mapProblemDetailsToApiError,
} from "@/infrastructure/adapters/problem-details";

/** Crée une instance axios avec la configuration commune à tous les clients HTTP de l'app. */
export function createHttpClient(): AxiosInstance {
  return axios.create({
    baseURL: appConfig.apiBaseUrl,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      // Empêche le navigateur de servir une réponse depuis le cache disque
      // (cas : réponse vide mise en cache avant l'initialisation du token MSAL)
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
    timeout: 30_000, // 30 secondes — aligné sur SLA export PDF (RM-11)
  });
}

function getRetryAfterSeconds(error: AxiosError): number | undefined {
  const headers = error.response?.headers as
    | Record<string, unknown>
    | undefined;
  const headerValue = headers?.["retry-after"] ?? headers?.["Retry-After"];
  if (typeof headerValue !== "string" && typeof headerValue !== "number") {
    return undefined;
  }

  const seconds = Number(headerValue);
  return Number.isFinite(seconds) && seconds >= 0
    ? Math.ceil(seconds)
    : undefined;
}

/**
 * Attache l'intercepteur de réponse partagé par tous les clients HTTP de
 * l'app : mappe toute erreur HTTP ou réseau vers un `ApiError` avec message
 * en français (RFC 7807). Le status (401, 403, etc.) reste porté par l'erreur
 * mappée ; c'est à l'appelant (ex. AuthContext pour le 401) de réagir dessus.
 */
export function attachProblemDetailsInterceptor(
  instance: AxiosInstance,
  onUnauthorized?: () => void,
): void {
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      if (error instanceof ApiError) {
        return Promise.reject(error);
      }

      const status = error.response?.status ?? 0;
      const body = error.response?.data;

      if (status === 401) {
        onUnauthorized?.();
      }

      if (error.response) {
        return Promise.reject(
          mapProblemDetailsToApiError(
            status,
            body,
            getRetryAfterSeconds(error),
          ),
        );
      }

      // Erreur réseau / timeout
      return Promise.reject(
        mapProblemDetailsToApiError(0, {
          detail: "Erreur réseau. Vérifiez votre connexion et réessayez.",
        }),
      );
    },
  );
}
